import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';

import { useAlertStore } from '../../store/providers/Alert';
import { useFeatureStore } from '../../store/providers/Feature';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './FeatureSchema';
import { modifyToInstance } from '../../lib/Formatter';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const FeatureModal = React.lazy(() => import('./FeatureModal'));

function Features() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, listRecords, newRecord, data, clearData } = useFeatureStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_SETTINGS_FEATURES;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (feature) => {
        setIsEditing(true);
        setSelectedItem(feature);
        selectRecord({ featureId: feature.Id });
    };

    const addedHandler = () => {
        setIsEditing(true);
        newRecord();
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        listRecords();
    };

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') { listRecords(); }
            if (data.status === 'success' && data.scope === 'list') {
                let result = data.body;
                if (Boolean(result && result.length)) {
                    result = result.map(record => {
                        if (record && record.Description) {
                            record.Description = modifyToInstance(record.Description, false);
                        }
                        return { ...record }
                    })
                }
                setListData(result);
            }
            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'feature', parent: 'Content', text: 'Features', path: window.location.pathname }));
        listRecords();
        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            isEditing && <FeatureModal
                                canEdit={canEdit}
                                show={isEditing}
                                title={`Edit Feature: ${selectedItem.Name}`}
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Features" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="Feature"
                                        keyField="Id"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        hideDelete={true}
                                        permissionEdit={canEdit}
                                        hideAdd={true}
                                        added={() => addedHandler()}
                                        selected={(feature) => selectedHandler(feature)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(Features));
